import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PARTNER_TARGETS, PARTNER_BENEFITS } from "@/data/staticContent";
import { Check, Building2 } from "lucide-react";

const INITIAL = {
  organization_name: "", country: "", contact_person: "", email: "", phone: "",
  organization_type: "", number_of_students: "", preferred_destination: "",
  preferred_program: "", proposed_travel_dates: "", partnership_interest: "", message: "",
};

const Input = ({ label, name, value, onChange, type = "text", required, testId }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}{required && " *"}</span>
    <input
      type={type} name={name} value={value} onChange={onChange} required={required}
      data-testid={testId}
      className="mt-1.5 w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm outline-none transition-colors"
    />
  </label>
);

export default function PartnerWithUs() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/partner-inquiries", form);
      toast.success("Thanks! Your partner inquiry has been received.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16" data-testid="partner-page">
      <div className="max-w-3xl mb-14">
        <p className="text-sm font-bold text-purple-600 uppercase tracking-widest mb-3">Partner With Us</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05]">
          Become a Global <span className="text-blue-600">Education Partner</span>
        </h1>
        <p className="mt-5 text-slate-600">Send your students on structured, meaningful, and affordable international learning experiences. We handle the destination logistics so you can focus on outcomes.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900 mb-4">Who we partner with</h2>
          <div className="flex flex-wrap gap-2 mb-10" data-testid="partner-targets">
            {PARTNER_TARGETS.map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-sm font-medium text-slate-700">
                <Building2 className="w-3.5 h-3.5" /> {t}
              </span>
            ))}
          </div>

          <h2 className="font-display text-2xl font-bold text-slate-900 mb-4">Partner benefits</h2>
          <ul className="space-y-2" data-testid="partner-benefits">
            {PARTNER_BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {b}
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={submit} className="rounded-3xl bg-white border border-slate-100 shadow-lg shadow-blue-500/5 p-6 md:p-8 grid gap-4" data-testid="partner-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Organization Name" name="organization_name" value={form.organization_name} onChange={onChange} required testId="partner-orgname" />
            <Input label="Country" name="country" value={form.country} onChange={onChange} testId="partner-country" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Contact Person" name="contact_person" value={form.contact_person} onChange={onChange} required testId="partner-contact" />
            <Input label="Email" name="email" type="email" value={form.email} onChange={onChange} required testId="partner-email" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Phone" name="phone" value={form.phone} onChange={onChange} testId="partner-phone" />
            <Input label="Organization Type" name="organization_type" value={form.organization_type} onChange={onChange} testId="partner-orgtype" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Number of Students" name="number_of_students" value={form.number_of_students} onChange={onChange} testId="partner-numstudents" />
            <Input label="Preferred Destination" name="preferred_destination" value={form.preferred_destination} onChange={onChange} testId="partner-pref-dest" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Preferred Program" name="preferred_program" value={form.preferred_program} onChange={onChange} testId="partner-pref-prog" />
            <Input label="Proposed Travel Dates" name="proposed_travel_dates" value={form.proposed_travel_dates} onChange={onChange} testId="partner-dates" />
          </div>
          <Input label="Partnership Interest" name="partnership_interest" value={form.partnership_interest} onChange={onChange} testId="partner-interest" />
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Message</span>
            <textarea
              name="message" value={form.message} onChange={onChange}
              data-testid="partner-message"
              rows={4}
              className="mt-1.5 w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm outline-none transition-colors resize-none"
            />
          </label>
          <button
            type="submit" disabled={submitting}
            data-testid="partner-submit"
            className="px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold text-sm shadow-lg shadow-slate-900/20 transition-colors"
          >
            {submitting ? "Submitting…" : "Submit Partner Inquiry"}
          </button>
        </form>
      </div>
    </div>
  );
}
